import { createClient } from "@supabase/supabase-js";
import type { WidgetConfigPayload } from "./types";

// These are injected at build time. Supabase URL + anon key are safe to expose.
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const HAS_VALID_CONFIG =
  Boolean(SUPABASE_URL) &&
  Boolean(SUPABASE_ANON_KEY) &&
  !SUPABASE_URL.includes("your-project") &&
  !SUPABASE_ANON_KEY.includes("your-anon-key");

if (!HAS_VALID_CONFIG) {
  console.warn(
    "[vertu-widget] VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY look unset or placeholder. Edit apps/widget/.env and restart the dev server.",
  );
}

export const supabase = createClient(SUPABASE_URL ?? "", SUPABASE_ANON_KEY ?? "", {
  auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
});

// NOTE: there is deliberately ONE Supabase client here, on the anon key.
// A second, session-persisting client existed briefly for guest OTP sign-in
// (20260806000050) and was removed with 20260806000110: guests hold an opaque
// capability token, not a Supabase account. See state/guest.tsx.

export type LoadResult =
  | { ok: true; payload: WidgetConfigPayload }
  | { ok: false; reason: "not_found" | "config_error" | "network_error"; detail?: string };

export async function loadWidgetConfig(
  tenantSlug: string,
  widgetSlug: string,
): Promise<LoadResult> {
  if (!HAS_VALID_CONFIG) {
    return {
      ok: false,
      reason: "config_error",
      detail:
        "VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is unset or still a placeholder. Edit apps/widget/.env and restart the dev server.",
    };
  }

  try {
    const { data, error } = await supabase.rpc("get_widget_config", {
      tenant_slug: tenantSlug,
      widget_slug: widgetSlug,
    });

    if (error) {
      console.error("[vertu-widget] Supabase RPC error:", error);
      return {
        ok: false,
        reason: "network_error",
        detail: error.message,
      };
    }
    if (!data) {
      return { ok: false, reason: "not_found" };
    }

    return { ok: true, payload: data as WidgetConfigPayload };
  } catch (err) {
    console.error("[vertu-widget] Network error:", err);
    return {
      ok: false,
      reason: "network_error",
      detail: (err as Error).message,
    };
  }
}
